import React, {useCallback, useEffect, useRef, useState} from 'react'

const enum SwipeDirection {
  Forward = 1,
  Backward = -1
}

type UseSwipeNavigationParams = {
  stoppagesCount: number
  isMobile: boolean
  onNext: (fromIndex: number) => Promise<void>
  onPrev: (fromIndex: number) => Promise<void>
}

type UseSwipeNavigationResult = {
  activeStep: number
  swipeDirectionRef: React.MutableRefObject<SwipeDirection>
  swipeContainerRef: React.RefObject<HTMLDivElement>
}

export const useSwipeNavigation = ({
  stoppagesCount,
  isMobile,
  onNext,
  onPrev
}: UseSwipeNavigationParams): UseSwipeNavigationResult => {
  const [activeStep, setActiveStep] = useState(0)

  const prevStepRef = useRef(activeStep)
  const swipeDirectionRef = useRef<SwipeDirection>(SwipeDirection.Forward)
  const touchStartXRef = useRef<number | null>(null)
  const touchStartYRef = useRef<number | null>(null)
  const swipeContainerRef = useRef<HTMLDivElement>(null)

  const handleSwipeChange = useCallback(
    async (newIndex: number): Promise<void> => {
      if (newIndex > prevStepRef.current) {
        await onNext(prevStepRef.current)
      } else if (newIndex < prevStepRef.current) {
        await onPrev(prevStepRef.current)
      }
      setActiveStep(newIndex)
      prevStepRef.current = newIndex
    },
    [onNext, onPrev]
  )

  useEffect(() => {
    if (!isMobile) return
    const el = swipeContainerRef.current
    if (!el) return

    const onTouchStart = (e: TouchEvent) => {
      touchStartXRef.current = e.touches[0].clientX
      touchStartYRef.current = e.touches[0].clientY
    }

    const onTouchEnd = (e: TouchEvent) => {
      if (touchStartXRef.current === null || touchStartYRef.current === null) return
      const deltaX = e.changedTouches[0].clientX - touchStartXRef.current
      const deltaY = e.changedTouches[0].clientY - touchStartYRef.current
      touchStartXRef.current = null
      touchStartYRef.current = null

      const isHorizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY) * 1.5
      if (!isHorizontalSwipe || Math.abs(deltaX) < 50) return

      if (deltaX < 0) {
        swipeDirectionRef.current = SwipeDirection.Forward
        void handleSwipeChange(Math.min(prevStepRef.current + 1, stoppagesCount - 1))
      } else {
        swipeDirectionRef.current = SwipeDirection.Backward
        void handleSwipeChange(Math.max(prevStepRef.current - 1, 0))
      }
    }

    el.addEventListener('touchstart', onTouchStart, {passive: true})
    el.addEventListener('touchend', onTouchEnd, {passive: true})
    return () => {
      el.removeEventListener('touchstart', onTouchStart)
      el.removeEventListener('touchend', onTouchEnd)
    }
  }, [isMobile, stoppagesCount, handleSwipeChange])

  return {activeStep, swipeDirectionRef, swipeContainerRef}
}
